import type {
  PmoFlowQuantiles,
  PmoFlowSegmentClass,
  PmoFlowSegmentKey,
  PmoFlowSegmentStat,
} from '../../../types/pmo-flow.js';

/**
 * フェーズ区間の時系列表示順。API のレスポンス順に依存せず、この順で並べ直して描画する。
 * 先頭の `reqAssignToReqDone` → `reqDoneToAssign` は開発 assign より前の要件定義工程で、
 * 工程順どおりバーの先頭に並べる。末尾の `integDoneToDesignCheck` → `designCheck` は
 * 結合テスト完了後のデザインチェック関門で、工程順どおりバーの最後に並べる。
 * @graph-connects none
 */
export const PMO_FLOW_SEGMENT_ORDER: readonly PmoFlowSegmentKey[] = [
  'reqAssignToReqDone',
  'reqDoneToAssign',
  'assignToStart',
  'startToDevDone',
  'devDoneToTestStart',
  'testExecution',
  'integDoneToDesignCheck',
  'designCheck',
];

/**
 * 区間区分ごとの系列色。開発フェーズは 待ち＝青 / 作業＝橙 の 2 色で「止まっている時間」と
 * 「動いている時間」を対比させ、mixed（全体スパン）は工程ではないため中立色にする。
 * 要件定義（開発 assign より前）は淡い緑で、開発フェーズの 2 色の対比に混ざらない色に置く
 * ——別予算・別担当で回る工程なので、待ち / 作業 の軸では読まない。
 * 値は CSS 変数参照で、ライト/ダークの明度差は `styles.css` 側で切り替える
 * （このバーは SVG ではなく DOM で描くため CSS 変数が解決される）。
 * @graph-connects none
 */
export const PMO_FLOW_CLASS_COLORS: Record<PmoFlowSegmentClass, string> = {
  requirement: 'var(--color-pmo-phase-requirement)',
  wait: 'var(--color-pmo-phase-wait)',
  work: 'var(--color-pmo-phase-work)',
  mixed: 'var(--color-pmo-phase-mixed)',
};

/**
 * 作業区間を合算した行の色。個別区間の作業色より一段濃い橙にして、
 * 区間 1 本の値ではなく「まとめた値」であることを色で区別する。
 * @graph-connects none
 */
export const PMO_FLOW_WORK_TOTAL_COLOR = 'var(--color-pmo-phase-work-strong)';

/**
 * 案件全体のスループット行の色。待ち（青）と作業（橙）の両方をまたぐ集計なので
 * どちらにも寄せず紫靛にして、「工程の速さ」ではなく「案件の実効速度」だと区別する。
 * @graph-connects none
 */
export const PMO_FLOW_PROJECT_TOTAL_COLOR = 'var(--color-pmo-phase-project-total)';

/** 未計測区間のプレースホルダー塗り（実データではないことを示す）。 @graph-connects none */
export const PMO_FLOW_PENDING_FILL = 'var(--color-bg-elevated)';

/** 未計測区間のプレースホルダー枠線色。 @graph-connects none */
export const PMO_FLOW_PENDING_STROKE = 'var(--color-text-muted)';

/** 未計測区間の破線パターン（実測値でないことを視覚的に区別する）。 @graph-connects none */
export const PMO_FLOW_PENDING_DASH = '4 3';

/**
 * 未計測区間に割り当てる公称幅の比率（計測済み合計に対する割合）。
 * 実データではないため軸目盛りとしては読めない旨をカード側で明記する。
 * @graph-connects none
 */
const PENDING_NOMINAL_RATIO = 0.08;

/** 計測済み合計が 0（＝全区間未計測）のときのプレースホルダー公称幅。 @graph-connects none */
const PENDING_NOMINAL_FALLBACK = 1;

/**
 * 区間統計を表示順に並べ替える。API が知らないキーを返した場合は末尾に回して落とさない。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMOフェーズ区間統計を画面の時系列表示順に並べ替える
 * @graph-connects none
 */
export function sortPmoFlowSegments(segments: PmoFlowSegmentStat[]): PmoFlowSegmentStat[] {
  return [...segments].sort((a, b) => {
    const ai = PMO_FLOW_SEGMENT_ORDER.indexOf(a.key);
    const bi = PMO_FLOW_SEGMENT_ORDER.indexOf(b.key);
    return (
      (ai === -1 ? PMO_FLOW_SEGMENT_ORDER.length : ai) -
      (bi === -1 ? PMO_FLOW_SEGMENT_ORDER.length : bi)
    );
  });
}

/**
 * 実測値を持つ区間かどうか。availability ではなく実際の値の有無で判定するため、
 * `sparse`（サンプル僅少）でも値があれば実測として描画できる。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMOフェーズ区間が実測の日数分位数を持つか判定する
 * @graph-connects none
 */
export function hasMeasuredDays(segment: PmoFlowSegmentStat): boolean {
  return segment.days !== null;
}

/**
 * 実測済み区間の p50 合計。積み上げバーの基準スケールとして使う。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 実測できているPMOフェーズ区間のp50日数を合計する
 * @graph-connects none
 */
export function sumMeasuredMedianDays(segments: PmoFlowSegmentStat[]): number {
  return segments.reduce((sum, segment) => sum + (segment.days?.p50 ?? 0), 0);
}

/**
 * 未計測区間に割り当てるプレースホルダー幅を算出する。実測合計に比例させることで、
 * 期間・スケールが変わってもプレースホルダーが潰れたり支配的になったりしない。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 未計測フェーズ区間のプレースホルダー公称幅を実測合計から算出する
 * @graph-connects none
 */
export function computePendingNominalWidth(segments: PmoFlowSegmentStat[]): number {
  const measuredTotal = sumMeasuredMedianDays(segments);
  if (measuredTotal <= 0) return PENDING_NOMINAL_FALLBACK;
  return Math.max(PENDING_NOMINAL_FALLBACK, measuredTotal * PENDING_NOMINAL_RATIO);
}

/**
 * 表示モード。実日数 / pt 正規化（pt/日）。
 */
export type PmoFlowValueMode = 'days' | 'perPoint';

/**
 * API の営業日/pt を画面表示用の pt/日（1営業日あたり何 pt 進むか）へ反転する。
 * 「小さいほど速い」より「大きいほど速い」の方が速度として直感的に読めるため。
 * 0 以下や非有限値は逆数が定義できないので undefined（表示はダッシュ）。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMOフェーズの規模正規化値を営業日/ptからpt/営業日へ反転する
 * @graph-connects none
 */
export function toPointsPerDay(daysPerPoint: number | undefined): number | undefined {
  if (daysPerPoint === undefined || !Number.isFinite(daysPerPoint) || daysPerPoint <= 0) {
    return undefined;
  }
  return 1 / daysPerPoint;
}

/**
 * 分位数の表示文字列。値が無い場合はダッシュ。
 * pt 正規化は pt/日 へ反転してから整形する。反転後は 2〜100 程度のレンジに散るため、
 * 実日数（小数1桁）より 1 桁多い小数2桁で、遅い工程の 0.5 pt/日 なども潰さずに出す。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMOフェーズ区間の分位数を表示モードに応じて反転・整形した文字列にする
 * @graph-connects toPointsPerDay [calls] 営業日/pt から pt/営業日 への反転
 */
export function formatQuantile(value: number | undefined, mode: PmoFlowValueMode = 'days'): string {
  const shown = mode === 'perPoint' ? toPointsPerDay(value) : value;
  if (shown === undefined || !Number.isFinite(shown)) return '—';
  return shown.toFixed(mode === 'perPoint' ? 2 : 1);
}

/**
 * pt/日 は日数の逆数なので、分位点の意味が上下反転する。日数側の遅い裾（p90）は
 * pt/日 では下位 10%（p10）にあたるため、列の位置（＝同じ案件群）は保ったまま
 * ラベルだけ読み替える。実日数モードではそのまま。
 * @graph-connects none
 */
const PER_POINT_QUANTILE_LABELS: Record<'p50' | 'p75' | 'p90', string> = {
  p50: 'p50',
  p75: 'p25',
  p90: 'p10',
};

/**
 * 分位数セルの見出し。pt/日 モードでは反転後の分位点名を返す。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMOフェーズ分位数セルの見出しを表示モードに応じた分位点名に読み替える
 * @graph-connects none
 */
export function quantileLabel(key: 'p50' | 'p75' | 'p90', mode: PmoFlowValueMode): string {
  return mode === 'perPoint' ? PER_POINT_QUANTILE_LABELS[key] : key;
}

/**
 * フェーズ境界（工程と工程の切れ目に立つ時点）のキー。
 * 区間名ではなくこの時点名をバーの目盛りとして描く。
 */
export type PmoFlowBoundaryKey =
  | 'reqAssign'
  | 'reqDone'
  | 'assign'
  | 'start'
  | 'devDone'
  | 'testStart'
  | 'integDone'
  | 'designCheckStart'
  | 'designCheckDone';

/**
 * 区間ごとの開始・終了境界。バーの目盛りはこの対応から組み立てるため、
 * 区間の追加・削除があっても目盛りが区間とずれない。
 * @graph-connects none
 */
export const PMO_FLOW_SEGMENT_BOUNDARIES: Record<
  PmoFlowSegmentKey,
  { from: PmoFlowBoundaryKey; to: PmoFlowBoundaryKey }
> = {
  reqAssignToReqDone: { from: 'reqAssign', to: 'reqDone' },
  reqDoneToAssign: { from: 'reqDone', to: 'assign' },
  assignToStart: { from: 'assign', to: 'start' },
  startToDevDone: { from: 'start', to: 'devDone' },
  devDoneToTestStart: { from: 'devDone', to: 'testStart' },
  testExecution: { from: 'testStart', to: 'integDone' },
  integDoneToDesignCheck: { from: 'integDone', to: 'designCheckStart' },
  designCheck: { from: 'designCheckStart', to: 'designCheckDone' },
};

/**
 * 積み上げバーの区間の種別。
 * - `measured`: 実測の p50 日数で幅を取る
 * - `pending`: 境界イベントが未収集で計測できない（破線の空枠）
 * - `illustrative`: そもそも計測しない参考表示（ハッチ塗り）
 */
export type PmoFlowSliceKind = 'measured' | 'pending' | 'illustrative';

/**
 * 計測しないが工程の連なりを読ませるためにバーへ差し込む区間。
 * デザインチェックは着手遷移を残さず完了する運用が多く、作業時間を測っても
 * 推定に使った営業日数が出るだけなので、幅は目安の色帯として置くに留める。
 * 結合テストの後の関門なので、バーの末尾に継ぎ足される。
 * @graph-connects none
 */
export const PMO_FLOW_ILLUSTRATIVE_SEGMENTS: readonly {
  key: PmoFlowSegmentKey;
  segmentClass: PmoFlowSegmentClass;
}[] = [{ key: 'designCheck', segmentClass: 'work' }];

/** 積み上げバー 1 区間分の描画情報。 */
export interface PmoFlowBarSlice {
  key: PmoFlowSegmentKey;
  segmentClass: PmoFlowSegmentClass;
  /** 区間の種別（実測 / 未計測 / 参考表示） */
  kind: PmoFlowSliceKind;
  /** 積み上げ幅。実測は p50 日数、それ以外は公称幅 */
  width: number;
  /** バー全体に対する幅の比率（0〜1） */
  ratio: number;
  /** 区間の開始境界 */
  from: PmoFlowBoundaryKey;
  /** 区間の終了境界 */
  to: PmoFlowBoundaryKey;
}

/** バーの目盛り 1 本分。 */
export interface PmoFlowBoundaryTick {
  /** バー左端からの位置（0〜1） */
  position: number;
  /**
   * この位置に立つ境界。前後の区間が連続していれば 1 件だが、
   * 間に表示しない工程（デザインチェックの作業区間）を挟むと 2 件になる
   */
  boundaries: PmoFlowBoundaryKey[];
}

/**
 * 積み上げバーに並べる区間を時系列順で選ぶ。境界が定義されていない未知のキーは除く。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMOフェーズ積み上げバーに並べる区間を時系列順に選ぶ
 * @graph-connects none
 */
export function selectPhaseBarSegments(segments: PmoFlowSegmentStat[]): PmoFlowSegmentStat[] {
  return sortPmoFlowSegments(segments).filter(
    (segment) => segment.key in PMO_FLOW_SEGMENT_BOUNDARIES,
  );
}

/**
 * 区間統計を積み上げバーの描画情報へ変換する。幅は実測なら p50 日数、
 * 未計測なら公称幅で、比率は目盛り位置の算出にそのまま使う。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMOフェーズ区間統計を積み上げバーの幅・比率・境界を持つ描画情報へ変換する
 * @graph-connects none
 */
export function buildPhaseBarSlices(segments: PmoFlowSegmentStat[]): PmoFlowBarSlice[] {
  const target = selectPhaseBarSegments(segments);
  if (target.length === 0) return [];

  const nominalWidth = computePendingNominalWidth(target);
  const sized: Omit<PmoFlowBarSlice, 'ratio'>[] = [];

  for (const [index, segment] of target.entries()) {
    const { from, to } = PMO_FLOW_SEGMENT_BOUNDARIES[segment.key];
    const measured = hasMeasuredDays(segment);
    sized.push({
      key: segment.key,
      segmentClass: segment.segmentClass,
      kind: measured ? 'measured' : 'pending',
      width: measured ? (segment.days?.p50 ?? 0) : nominalWidth,
      from,
      to,
    });

    // 次の区間との間が空くなら、そこに参考表示の工程があれば色帯で埋める。
    // 末尾（デザインチェック）は次が無いので、終端から始まる工程を継ぎ足す
    const next = target[index + 1];
    const nextFrom = next === undefined ? null : PMO_FLOW_SEGMENT_BOUNDARIES[next.key].from;
    if (nextFrom === to) continue;
    const filler = findIllustrativeSegment(to, nextFrom);
    if (filler === null) continue;
    sized.push({
      ...filler,
      kind: 'illustrative',
      width: nominalWidth,
      from: to,
      to: PMO_FLOW_SEGMENT_BOUNDARIES[filler.key].to,
    });
  }

  const total = sized.reduce((sum, item) => sum + item.width, 0);
  return sized.map((item) => ({
    ...item,
    ratio: total > 0 ? item.width / total : 1 / sized.length,
  }));
}

/**
 * 指定した境界の間を埋める参考表示の区間を探す。無ければ null。
 * @graph-connects none
 */
function findIllustrativeSegment(
  from: PmoFlowBoundaryKey,
  to: PmoFlowBoundaryKey | null,
): { key: PmoFlowSegmentKey; segmentClass: PmoFlowSegmentClass } | null {
  return (
    PMO_FLOW_ILLUSTRATIVE_SEGMENTS.find((candidate) => {
      const boundaries = PMO_FLOW_SEGMENT_BOUNDARIES[candidate.key];
      // to が null（末尾）なら、終端から始まる工程を継ぎ足す
      return boundaries.from === from && (to === null || boundaries.to === to);
    }) ?? null
  );
}

/**
 * バーの目盛り位置を組み立てる。左端に最初の区間の開始境界、以降は各区間の
 * 終了境界を累積比率の位置に置く。次の区間の開始境界が一致しない場合
 * （表示しない工程を挟む場合）は同じ位置に 2 件並べる。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMOフェーズ積み上げバーの工程名目盛りを累積比率から組み立てる
 * @graph-connects none
 */
export function buildPhaseBoundaryTicks(slices: PmoFlowBarSlice[]): PmoFlowBoundaryTick[] {
  const [head] = slices;
  if (head === undefined) return [];

  const ticks: PmoFlowBoundaryTick[] = [{ position: 0, boundaries: [head.from] }];
  let position = 0;
  for (const [index, slice] of slices.entries()) {
    position += slice.ratio;
    const next = slices[index + 1];
    ticks.push({
      position: Math.min(position, 1),
      boundaries: next === undefined || next.from === slice.to ? [slice.to] : [slice.to, next.from],
    });
  }
  return ticks;
}

/**
 * バーの区間と一覧の行を突き合わせるためのハイライト対象。
 * どちらか一方に hover したとき、同じ母集団・同じ区間の相手を淡く光らせる。
 */
export interface PmoFlowHighlightProps {
  /** ハイライト中の区間（無ければ null） */
  highlightedKey?: PmoFlowSegmentKey | null;
  /** hover 開始/終了。終了時は null を渡す */
  onHighlight?: (key: PmoFlowSegmentKey | null) => void;
}

/**
 * ハイライト時に区間色を淡く敷いた背景・枠線を返す。区間色をそのまま薄めることで、
 * バー側とどの区間が対応しているかを色で辿れるようにする。
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business PMOフェーズ一覧の行をハイライトする淡い背景色と枠線色を区間色から作る
 * @graph-connects none
 */
export function highlightStyle(color: string): {
  backgroundColor: string;
  borderColor: string;
} {
  return {
    backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)`,
    borderColor: `color-mix(in srgb, ${color} 45%, transparent)`,
  };
}

/**
 * 待ち時間パネルの p50 マーカー位置（0〜1）。トラックは 0〜p90 のスケールで、
 * p50 がどのあたりに沈んでいるかで尾の長さを読ませる。
 * p90 が 0（＝待ちがほぼ発生していない）なら比率が定義できないので 0 を返す
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 待ち時間パネルのトラック上で中央値が占める位置の比率を算出する
 * @graph-connects none
 */
export function waitMedianRatio(days: PmoFlowQuantiles): number {
  if (!Number.isFinite(days.p90) || days.p90 <= 0) {
    return 0;
  }
  return Math.min(1, Math.max(0, days.p50 / days.p90));
}
